import React, { useContext, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Headline from "~/assets/js/components/Typography/Headline";
import Button from "~/assets/js/components/Button";
import { PlusCircle, ShoppingBag, X } from "react-feather";
import { ACTIONS, ProductsDetailsContext } from "../../ProductsProvider";
import i18n from "~/assets/js/Helpers/i18n";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import QuantitySelectButton from "~/assets/js/components/Button/QuantitySelectButton";
import BasicIngredients from "../Ingredients/BasicIngredients";
import ExtraIngredients from "../Ingredients/ExtraIngredients";
import OverviewIngredients from "../Ingredients/OverviewIngredients";
import useBodyScrollLock from "../../../../Helpers/hooks/useBodyScrollLock";
import { GREEN_CLUB_URL } from "~/assets/js/global/greenClub";
import GreenClubTag from "~/assets/js/components/GreenClub/GreenClubTag";

const MINIMUM_POPUP_HEIGHT = 300;
export const VIEWS = {
    BASIC: "basic",
    EXTRA: "extra",
    OVERVIEW: "overview",
};

const StickyFooter = ({
    product,
    selectDressingsAction,
    extended = false,
    setExtended,
    discounted,
    selectGreenClubPrice,
}) => {
    const {
        state,
        updateProductState,
        submit,
        isSubmitting,
        fullPrice,
        greenClubMembershipPrice,
        isMainDish,
        inStock,
        isDiscoverMode,
        triggerAddressPopup,
    } = useContext(ProductsDetailsContext);
    const { dressings } = state;
    const { isDesktop } = useViewport();
    const [currentView, setCurrentView] = useState(VIEWS.BASIC);
    useBodyScrollLock(extended);

    const dressingsText = useMemo(() => {
        if (dressings.length <= 0) {
            return i18n.t("dressings.footer.noDressing");
        }
        if (dressings.length === 1) {
            return dressings[0].name;
        }

        const countOfDressings = dressings.reduce((acc, current) => {
            return acc + current.quantity;
        }, 0);

        return i18n.t("dressings.footer.multipleDressings", {
            count: countOfDressings,
        });
    }, [dressings]);

    // Go to first page on close
    // Handle body scroll locks
    useEffect(() => {
        if (!extended) setCurrentView(VIEWS.BASIC);
        return () => {
            setCurrentView(VIEWS.BASIC);
        };
    }, [extended]);

    const renderExtendedView = () => {
        if (!extended) return null;
        switch (currentView) {
            case VIEWS.EXTRA:
                return (
                    <ExtraIngredients
                        handleClose={() => setExtended(false)}
                        onClickForward={() => setCurrentView(VIEWS.OVERVIEW)}
                    />
                );
            case VIEWS.OVERVIEW:
                return (
                    <OverviewIngredients
                        handleClickBack={() => setCurrentView(VIEWS.EXTRA)}
                        handleClose={() => setExtended(false)}
                    />
                );
            case VIEWS.BASIC:
            default:
                return (
                    <BasicIngredients
                        handleClose={() => setExtended(false)}
                        onClickExtras={() => {
                            if (isDiscoverMode) {
                                triggerAddressPopup();
                                return;
                            }
                            setCurrentView(VIEWS.EXTRA);
                        }}
                    />
                );
        }
    };

    const updateProductQuantity = (value) => {
        if (isDiscoverMode) {
            triggerAddressPopup();
            return;
        }

        updateProductState({ type: ACTIONS.UPDATE_QUANTITY, payload: value });
    };

    const handleSubmit = () => {
        setExtended(false);
        submit();
    };

    return (
        <>
            {extended && (
                <div className="popup__backdrop">
                    <X
                        className="close-sticky-footer"
                        size={32}
                        onClick={() => setExtended(false)}
                    />
                </div>
            )}
            <div
                id="sticky-footer"
                className={`products-sticky-footer ${
                    extended ? "extended" : ""
                }`}>
                <div style={{ position: "relative", height: "100%" }}>
                    {!isDesktop && isMainDish && (
                        <DraggableFooter
                            setExtended={setExtended}
                            extended={extended}
                        />
                    )}
                    {extended ? (
                        <div className="extended-content">
                            {renderExtendedView()}
                        </div>
                    ) : (
                        <div className="products-sticky-footer__content">
                            <div className="about-product">
                                <img
                                    src={product.images[0].src}
                                    alt="product-image"
                                    width="40"
                                    height="40"
                                />
                                <div className="about-product__main">
                                    <Headline size={6} className="title">
                                        {product.title}
                                    </Headline>
                                    {isMainDish && (
                                        <span
                                            className="add-dressing"
                                            onClick={selectDressingsAction}>
                                            {dressingsText}
                                            {isDesktop && (
                                                <PlusCircle size={18} />
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {isDesktop && (
                                <div className="price-overview">
                                    <div className="price-overview__content">
                                        <Headline size={6} className="price">
                                            {discounted && isMainDish
                                                ? greenClubMembershipPrice
                                                : fullPrice}
                                        </Headline>
                                        <span className="tax">
                                            {i18n.t("productInfo.taxRate", {
                                                rate: product.taxRate,
                                            })}
                                        </span>
                                    </div>
                                    {discounted && isMainDish && (
                                        <GreenClubTag color="green" />
                                    )}
                                </div>
                            )}
                            <div className="to-basket">
                                {isDesktop ? (
                                    selectGreenClubPrice ? (
                                        <Button
                                            customStyle="green-club__checkout-cta"
                                            label={i18n.t(
                                                "productDetails.greenClub.onboardingPopup.cta"
                                            )}
                                            size="SM"
                                            variant="primary"
                                            href={GREEN_CLUB_URL}
                                            onClick={handleSubmit}
                                        />
                                    ) : (
                                        <QuantitySelectButton
                                            onValueChange={
                                                updateProductQuantity
                                            }
                                            quantity={state.quantity}
                                            onClick={handleSubmit}
                                            isLoading={isSubmitting}
                                            customStyle={
                                                inStock || isDiscoverMode
                                                    ? ""
                                                    : "disabled"
                                            }
                                        />
                                    )
                                ) : // MOBILE
                                selectGreenClubPrice ? (
                                    <Button
                                        loading={isSubmitting}
                                        loadingLabel=""
                                        label={""}
                                        icon={<ShoppingBag />}
                                        size="SM"
                                        disabled={true}
                                        href={GREEN_CLUB_URL}
                                        customStyle={`sticky-footer-btn`}
                                    />
                                ) : (
                                    <Button
                                        loading={isSubmitting}
                                        loadingLabel=""
                                        label={""}
                                        icon={<ShoppingBag />}
                                        size="SM"
                                        onClick={handleSubmit}
                                        customStyle={`sticky-footer-btn ${
                                            inStock || isDiscoverMode
                                                ? ""
                                                : "disabled"
                                        }`}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const DraggableFooter = ({ setExtended, extended }) => {
    const setHeight = (height) => {
        const windowHeight = window.innerHeight;
        const popupHeight = windowHeight - height;
        document.getElementById(
            "sticky-footer"
        ).style.height = `${popupHeight}px`;
    };

    const onDragEnd = (height) => {
        const windowHeight = window.innerHeight;
        const popupHeight = windowHeight - height;

        document.getElementById("sticky-footer").style.removeProperty("height");

        setExtended(popupHeight > MINIMUM_POPUP_HEIGHT);
    };

    return (
        <div
            className="draggable"
            onClick={() => {
                setExtended(!extended);
            }}
            onTouchMove={(event) => {
                event.persist();
                setHeight(event.touches[0].clientY);
            }}
            onTouchEnd={(event) => {
                event.persist();
                onDragEnd(event.changedTouches[0].clientY);
            }}
        ></div>
    );
};

DraggableFooter.propTypes = {
    setExtended: PropTypes.func,
    extended: PropTypes.bool,
};

StickyFooter.propTypes = {
    product: PropTypes.object,
    selectDressingsAction: PropTypes.func,
    extended: PropTypes.bool.isRequired,
    setExtended: PropTypes.func.isRequired,
    discounted: PropTypes.bool,
    selectGreenClubPrice: PropTypes.func,
};

export default StickyFooter;
